import {XIcon} from '@heroicons/react/outline';
import React from 'react';

import Button from '@/components/common/Button';
import {StatusIndicator} from '@/components/StatusIndicator';
import type {
  AfriexTransactionChannel,
  AfriexTransactionProcessor,
  AfriexTransactionType,
  ProcessorWithEmpty,
} from '@/types';
import {capitalizeWords} from '@/utils/strings';

interface ChannelProcessorDisplayProps {
  channel: AfriexTransactionChannel;
  currentProcessor?: ProcessorWithEmpty;
  onRemove?: (
    transactionType: AfriexTransactionType,
    channel: AfriexTransactionChannel,
  ) => void;
  transactionType?: AfriexTransactionType;
  activeAvailableProcessors?: AfriexTransactionProcessor[];
  onRemoveAvailableProcessor?: (
    processorName: AfriexTransactionProcessor,
  ) => void;
  /**
   * Processors switched off for this corridor, mapped to why (e.g. "retired").
   * Routing skips them, so a configured one is flagged rather than shown as
   * working. Optional: screens without capability data leave it out.
   */
  switchedOff?: ReadonlyMap<string, string>;
}

const ChannelProcessorDisplay: React.FC<ChannelProcessorDisplayProps> = ({
  channel,
  currentProcessor,
  onRemove,
  transactionType,
  activeAvailableProcessors,
  onRemoveAvailableProcessor,
  switchedOff,
}) => {
  const hasProcessor = !!currentProcessor;
  const channelName = capitalizeWords(channel?.replace(/_/g, ' '));
  const currentNote = currentProcessor
    ? switchedOff?.get(currentProcessor)
    : undefined;
  // Routing tries the current processor, then the listed ones, skipping any
  // that are switched off.
  const hasFallback = (activeAvailableProcessors ?? []).some(
    (p) => p !== currentProcessor && !switchedOff?.has(p),
  );

  let currentLabel: React.ReactNode = 'Not configured';
  if (hasProcessor && currentNote) {
    currentLabel = (
      <span
        className="text-amber-600 dark:text-amber-400 font-semibold"
        title={`Switched off: ${currentNote}`}
      >
        {currentProcessor} (!) {currentNote}
      </span>
    );
  } else if (hasProcessor) {
    currentLabel = (
      <span className="text-green-600 dark:text-green-400 font-semibold">
        {currentProcessor}
      </span>
    );
  }

  const handleRemove = () => {
    if (onRemove && transactionType) {
      onRemove(transactionType, channel);
    }
  };

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center">
          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {channelName}
          </h4>
          <StatusIndicator hasValue={hasProcessor} />
        </div>

        {hasProcessor && (
          <span className="text-xs text-green-600 dark:text-green-400 font-medium">
            Configured
          </span>
        )}
      </div>

      <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 flex items-center justify-between">
        <span>Current: {currentLabel}</span>

        {hasProcessor && onRemove && (
          <Button
            variant="unStyled"
            size="sm"
            colorScheme="red"
            onClick={handleRemove}
            className="ml-2 text-xs h-6 text-red-700"
            type="button"
          >
            Remove
          </Button>
        )}
      </div>

      {currentNote && (
        <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
          {hasFallback
            ? 'Switched off, so transactions skip it and use the next processor listed below.'
            : 'Switched off, and no other processor listed here can take over: this corridor cannot process transactions.'}
        </p>
      )}

      {/* Section 2: Active available processor(s) — per channel */}
      <div className="py-1 text-gray-500 dark:text-gray-400">
        <p className="text-xs mb-1">Active available processor(s)</p>
        {activeAvailableProcessors?.length === 0 ||
        !activeAvailableProcessors ? (
          <p className="text-xs text-gray-500/80">NONE</p>
        ) : (
          <ul className="flex flex-row flex-wrap gap-2">
            {activeAvailableProcessors?.map((p) => (
              <li
                key={p}
                title={
                  switchedOff?.has(p)
                    ? `Switched off: ${switchedOff.get(p)}`
                    : undefined
                }
                className={`flex items-center gap-1 p-1 px-2 rounded text-xs ${
                  switchedOff?.has(p)
                    ? 'border border-dashed border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500'
                    : 'bg-indigo-200 dark:bg-indigo-900 dark:text-white'
                }`}
              >
                {switchedOff?.has(p) ? `${p} (!) ${switchedOff.get(p)}` : p}
                <XIcon
                  className="w-4 h-4 text-red-700 cursor-pointer"
                  onClick={() => onRemoveAvailableProcessor?.(p)}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default ChannelProcessorDisplay;
